/**
 * Presentation of the declared exposure timeline, not a second scope resolver.
 *
 * The panel resolves the existing reveal/shared-view marks once per render.
 * Asset rows, choices and per_asset bindings cannot move a question to another
 * phase: its declared zone stays its zone. Whether lines show is the one
 * owner's (`exposureZonesOf`: a valid line and a stimulus shown; catalog
 * materials such as pains are not stimulus, so a study with none shows no
 * exposure at all); how they read follows the StudySpec's stimulus count and
 * design.
 *
 * What each strip and question SAY twins see is the runner's delivery, read
 * per question from shared-types `twinExposureOf` (pinned to the engine by
 * `tests/fixtures/study-spec/twin-exposure-cases.json`), never detected here.
 *
 * Incompatible structures are reported by shared exposure validation.
 */
import type { TFunction } from "i18next";

import {
  exposureZoneAt,
  exposureZonesOf,
  studySpecOf,
  type QuestionnaireExposureMarkIndexes,
  type QuestionnaireExposureZone,
  type StudySpec,
  type TestedAssetType,
  type TwinExposure,
} from "@digital-twins/shared-types";

import { assetTypeNouns } from "./conceptAssets";

/** What the builder can truthfully say about exposure for one render. */
export interface ExposureView {
  /** Stimulus versions the study shows: 1 or more. */
  versions: number;
  /**
   * The two lines are one exposure transition: a comparative design, whose
   * pair the workspace moves together (`setExposureMark`).
   */
  linesMoveTogether: boolean;
}

/**
 * Null when the draft has no exposure to order (`exposureZonesOf`, the one
 * owner: no valid line, or a study that shows twins no stimulus): no lines.
 */
export function exposureViewOf(
  draft: { studySpec?: StudySpec | null } | null | undefined,
): ExposureView | null {
  const spec = studySpecOf(draft);
  if (!spec || exposureZonesOf(draft) === null) {
    return null;
  }
  return {
    versions: spec.resolved.stimulus.count,
    linesMoveTogether: spec.resolved.design === "comparative",
  };
}

/**
 * Whether the shared-view line shows. With two or more versions it always
 * does. With one it shows wherever it is not at the end: the two zones still
 * decide what a question shows, and every mark that limits where the reveal
 * can go is on screen. At the end it marks nothing.
 */
export function showsSharedViewLine(
  view: ExposureView,
  indexes: QuestionnaireExposureMarkIndexes,
  questionCount: number,
): boolean {
  return view.versions > 1 || indexes.sharedViewIndex < questionCount;
}

/**
 * Whether the two lines stand together as one strip: both show, the design
 * moves them together, and nothing sits between them. The panel draws one
 * strip for them and `setExposureMark` moves the pair when that strip moves.
 */
export function exposureLinesAreOneStrip(
  view: ExposureView,
  indexes: QuestionnaireExposureMarkIndexes,
  questionCount: number,
): boolean {
  return (
    showsSharedViewLine(view, indexes, questionCount) &&
    view.linesMoveTogether &&
    indexes.sharedViewIndex === indexes.revealIndex + 1
  );
}

/**
 * What a strip says about the zone below it:
 * - `lanes`: the lane repeats over two or more versions, one isolated pass
 *   each (`TwinExposure.lanes`);
 * - `shared`: the shared view shows two or more versions at once, each twin in
 *   its own rotated order (survey-engine `twin_stimulus_order`);
 * - `together`: a lane that runs once shows two or more versions on one
 *   question, in one order;
 * - `one`: one asset is on screen; its tile says what, so no words;
 * - `none`: no question there shows anything.
 */
export type ExposureStripKind = "lanes" | "shared" | "together" | "one" | "none";

/** One post-reveal zone: what it shows twins, and how its strip says so. */
export interface ExposureZoneShows {
  kind: ExposureStripKind;
  /** The asset ids on screen somewhere in the zone. */
  assetIds: ReadonlySet<string>;
}

/** What a strip says: its bold label (null: the mark's own name) and the words after "·". */
export interface ExposureStripWords {
  label: string | null;
  detail: string | null;
}

/**
 * The words of the strip over a zone (owner wording, 2026-10-14). The
 * assets are named by their family's nouns (`assetTypeNouns`; a zone mixing
 * families says "asset"), and the shared view counts the assets it shows.
 */
export function exposureStripWords(
  shows: ExposureZoneShows | null,
  assets: ReadonlyArray<{ type: string }>,
  t: TFunction<"app">,
): ExposureStripWords {
  if (!shows || shows.kind === "one") {
    return { label: null, detail: null };
  }
  if (shows.kind === "together") {
    return { label: null, detail: t("questionnaire.exposureStripTogether") };
  }
  if (shows.kind === "none") {
    return { label: null, detail: t("questionnaire.exposureStripNone") };
  }
  const types = [...new Set(assets.map((asset) => asset.type))];
  const nouns =
    types.length === 1
      ? assetTypeNouns(t, types[0] as TestedAssetType)
      : {
          singular: t("questionnaire.assetsFallback.singular"),
          plural: t("questionnaire.assetsFallback.plural"),
        };
  // Each language's copy picks the case it needs: a mid-sentence noun, or a
  // noun that opens the label.
  const words = {
    singular: nouns.singular,
    Singular: nouns.singular.charAt(0).toUpperCase() + nouns.singular.slice(1),
    plural: nouns.plural.toLowerCase(),
    Plural: nouns.plural,
    count: shows.assetIds.size,
  };
  return shows.kind === "lanes"
    ? {
        label: t("questionnaire.exposureStripLanesLabel", words),
        detail: t("questionnaire.exposureStripLanesDetail", words),
      }
    : {
        label: t("questionnaire.exposureStripSharedLabel", words),
        detail: t("questionnaire.exposureStripSharedDetail", words),
      };
}

/**
 * What the lane zone and the shared view show, read from the runner's
 * delivery (`TwinExposure`); null for a zone with no question. A lane that
 * repeats over two or more versions shows them one per pass; any other zone
 * shows what its questions put on screen.
 */
export function exposureZonesShowOf(
  questions: ReadonlyArray<{ stableKey: string }>,
  indexes: QuestionnaireExposureMarkIndexes,
  exposure: TwinExposure,
): Record<"lane" | "shared_view", ExposureZoneShows | null> {
  const zoneOf = (zone: "lane" | "shared_view"): ExposureZoneShows | null => {
    const keys = questions
      .filter((_, index) => exposureZoneAt(index, indexes) === zone)
      .map((question) => question.stableKey);
    if (keys.length === 0) {
      return null;
    }
    if (zone === "lane" && exposure.lanes.length > 1) {
      return { kind: "lanes", assetIds: new Set(exposure.lanes) };
    }
    const assetIds = new Set(keys.flatMap((key) => exposure.onScreen.get(key) ?? []));
    const kind: ExposureStripKind =
      assetIds.size === 0
        ? "none"
        : assetIds.size === 1
          ? "one"
          : zone === "shared_view"
            ? "shared"
            : "together";
    return { kind, assetIds };
  };
  return { lane: zoneOf("lane"), shared_view: zoneOf("shared_view") };
}

/**
 * Whether a question is asked with the assets off screen: its zone shows twins
 * something and the question shows nothing (recalled, bound to no asset in the
 * shared view, unbound in a repeating lane, or naming no stimulus in a lane
 * that runs once). The one per-question exposure fact the builder states.
 */
export function askedOffScreen(
  stableKey: string,
  zone: QuestionnaireExposureZone,
  zones: Record<"lane" | "shared_view", ExposureZoneShows | null>,
  exposure: TwinExposure,
): boolean {
  if (zone === "pre" || !zones[zone]?.assetIds.size) {
    return false;
  }
  return (exposure.onScreen.get(stableKey) ?? []).length === 0;
}
